import "./LocationsPage.scss";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownOutlined,
  EllipsisOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Dropdown, Menu, Modal, Table } from "antd";
import { LocationDto } from "../../../../generated/dashboard-client";
import { mobileState } from "../../../../state/atoms";
import { useRecoilValue } from "recoil";
import { useState } from "react";
import { States } from "../../../../util/countries";

interface LocationsTableProps {
  locations: LocationDto[];
  primaryLocation: LocationDto;
  disableRemove: boolean;
  onSortClick: (sort: string) => void;
  onEdit: (location: LocationDto) => void;
  onRemove: (location: LocationDto) => void;
  onSetPrimary: (location: LocationDto) => void;
  pagination?: any;
  locationUserCounts: Record<number, number>;
  onUsersClick: (location: LocationDto) => void;
}

export function LocationsTable({
  locations,
  primaryLocation,
  disableRemove,
  onSortClick,
  onEdit,
  onRemove,
  onSetPrimary,
  pagination,
  locationUserCounts,
  onUsersClick,
}: LocationsTableProps) {
  const mobile = useRecoilValue(mobileState);
  const [mobileSortDrawer, setMobileSortDrawer] = useState(false);
  const [mobileOptionsDrawer, setMobileOptionsDrawer] = useState<string | null>(null);

  function getStateAbbreviation(value: string) {
    return States[value] || value;
  }

  function formatAddress(record: LocationDto) {
    const parts = [];
    if (record.address) parts.push(record.address);
    if (record.city) parts.push(record.city);
    if (record.state) parts.push(getStateAbbreviation(record.state));
    if (record.zip) parts.push(record.zip);
    return parts.join(", ");
  }

  const menuSortItems = [
    {
      key: "asc",
      label: "Alphabetical (A-Z)",
      icon: <ArrowUpOutlined style={{ fontSize: "18px" }} />,
    },
    {
      key: "desc",
      label: "Alphabetical (Z-A)",
      icon: <ArrowDownOutlined style={{ fontSize: "18px" }} />,
    },
  ];

  function getMenuItems(location: LocationDto) {
    const items = [
      { key: "edit", label: "Edit Location" },
    ];

    if (!disableRemove) {
      items.push({ key: "delete", label: "Delete Location" });
    }

    if (location.internalId !== primaryLocation.internalId) {
      items.push({ key: "primary", label: "Make Primary Location" });
    }

    return items;
  }

  function handleMenuClick(key: string, location: LocationDto) {
    if (key === "edit") {
      onEdit(location);
    } else if (key === "delete") {
      Modal.confirm({
        title: "Are you sure you want to delete this location?",
        onOk() {
          onRemove(location);
        },
      });
    } else if (key === "primary") {
      onSetPrimary(location);
    }
  }

  const columns = [
    {
      title: () => (
        mobile ? (
          <>
            <span style={{ cursor: "pointer" }}>
              Location Name <DownOutlined
                onClick={() => setMobileSortDrawer(true)}
                style={{ fontSize: "14px", transform: "translate(0, 2px)", marginLeft: "4px" }}
              />
            </span>
            <Drawer
              open={mobileSortDrawer}
              placement="bottom"
              onClose={() => setMobileSortDrawer(false)}
              title={"Sort by"}
              className="mobile-locations-drawer"
              closeIcon={<DownOutlined />}
              height={230}
            >
              <Menu
                items={menuSortItems}
                onClick={({ key }) => {
                  onSortClick(key);
                  setMobileSortDrawer(false);
                }}
              />
            </Drawer>
          </>
        ) : (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: menuSortItems,
              onClick: ({ key }) => onSortClick(key),
            }}
          >
            <span style={{ cursor: "pointer" }}>
              Name <DownOutlined style={{ fontSize: "14px", transform: "translate(0, 2px)", marginLeft: "4px" }} />
            </span>
          </Dropdown>
        )
      ),
      dataIndex: "name",
      key: "name",
      render: (_: any, record: LocationDto) => (
        <div className="location-name-cell">
          <div className="location-name-row">
            <span className="location-name">{record.name}</span>
            {/*{record.internalId === primaryLocation.internalId && (*/}
            {/*  <span className="primary-badge"><CheckOutlined /> Primary</span>*/}
            {/*)}*/}
          </div>
          {mobile && (
            <>
              <span className="location-address-mob">{formatAddress(record)}</span>
              <button className="location-users-mob location-users-link" onClick={() => onUsersClick(record)}>
                <UserOutlined /> {(locationUserCounts[record.internalId!] || 0) + 1}
              </button>
            </>
          )}
        </div>
      ),
    },
    {
      title: "Address",
      key: "address",
      render: (_: any, record: LocationDto) => (
        <span>{formatAddress(record)}</span>
      ),
    },
    {
      title: "# of Users",
      key: "users",
      align: "center" as const,
      render: (_: any, record: LocationDto) => (
        <button className="location-users-link" onClick={() => onUsersClick(record)}>
          {(locationUserCounts[record.internalId!] || 0) + 1}
        </button>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: LocationDto) => {
        if (mobile) {
          return (
            <>
              <Button className={"location-action-btn"} onClick={() => setMobileOptionsDrawer(record.id || null)}>
                <EllipsisOutlined />
              </Button>
              <Drawer
                open={mobileOptionsDrawer === record.id}
                placement="bottom"
                onClose={() => setMobileOptionsDrawer(null)}
                title={"More"}
                className="mobile-locations-drawer"
                closeIcon={<DownOutlined />}
                height={220}
              >
                <Menu
                  items={getMenuItems(record)}
                  onClick={({ key }) => {
                    setMobileOptionsDrawer(null);
                    handleMenuClick(key, record);
                  }}
                />
              </Drawer>
            </>
          );
        }

        return (
          <Dropdown
            overlayClassName="location-actions-dropdown"
            menu={{
              items: getMenuItems(record),
              onClick: ({ key }) => handleMenuClick(key, record),
            }}
          >
            <Button className={"location-action-btn"}>
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={locations}
      pagination={pagination || false}
      rowKey="id"
    />
  );
}
